/**
 * DataTable (P27).
 *
 * Lifted verbatim from `sample-wireframe/client/src/pages/Home.tsx`. The
 * `.table-card` / `.compact` / `.mono-strong` / `.table-arrow` classes
 * already live in the lifted `apps/web/src/styles/index.css`.
 *
 * Heuristics ported as-is:
 *   - Columns named `status` / `state` / `health` / `risk` / `breaker`
 *     auto-render their cell as a `<StatusPill>`.
 *   - Cell values that match a known status vocabulary
 *     (Allowed/Denied/Review/Active/Blocked/Approved) also auto-pill.
 *   - First column renders in mono for IDs / tool names.
 */
import type { JSX } from 'react';
import { ChevronRight } from 'lucide-react';

import { cn } from '@/lib/utils.js';

import { StatusPill, statusToneFrom } from './StatusPill.js';

export type RowValue = string | number;
export type DataRow = Record<string, RowValue>;

interface DataTableProps {
  columns: readonly string[];
  rows: readonly DataRow[];
  onRow?: (row: DataRow) => void;
  compact?: boolean;
}

const STATUS_COLUMNS = new Set(['status', 'state', 'health', 'risk', 'breaker']);
const STATUS_VALUES = new Set(['Allowed', 'Denied', 'Review', 'Active', 'Blocked', 'Approved']);

export function DataTable({ columns, rows, onRow, compact }: DataTableProps): JSX.Element {
  return (
    <div className={cn('table-card', compact && 'compact')}>
      <table>
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column}>{column}</th>
            ))}
            {onRow && <th />}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            const firstColumn = columns[0];
            const rowKey =
              firstColumn !== undefined ? `${row[firstColumn]}-${index}` : `row-${index}`;
            return (
              <tr
                key={rowKey}
                onClick={onRow ? () => onRow(row) : undefined}
                style={onRow ? { cursor: 'pointer' } : undefined}
              >
                {columns.map((column) => {
                  const value = row[column] ?? '';
                  const shouldPill =
                    STATUS_COLUMNS.has(column.toLowerCase()) || STATUS_VALUES.has(String(value));
                  return (
                    <td key={column}>
                      {shouldPill ? (
                        <StatusPill label={String(value)} tone={statusToneFrom(value)} />
                      ) : (
                        <span className={column === firstColumn ? 'mono-strong' : ''}>
                          {String(value)}
                        </span>
                      )}
                    </td>
                  );
                })}
                {onRow && (
                  <td className="table-arrow">
                    <ChevronRight size={15} />
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
